import { useCallback, useMemo, useRef, useState } from 'react';
import {
    IconBan,
    IconGrid4x4,
    IconGridDots,
    IconPhoto,
    IconPlus,
    IconSlash,
    IconX,
} from '@tabler/icons-react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import {
    BG_PATTERNS,
    DOT_STYLES,
    ERROR_LEVELS,
    EYE_BALL_STYLES,
    EYE_FRAME_STYLES,
    GRADIENT_TYPES,
} from './qrCodeEngine';
import { FRAME_TYPES } from './qrFrames';
import { COLOR_PALETTES } from './qrStylePresets';
import { LOGO_PRESETS, LogoPresetTile, findLogoPreset } from './qrLogoPresets';
import { DotThumb, EyeBallThumb, EyeFrameThumb, FrameThumb } from './QrStyleThumbs';
import {
    ColorField,
    Field,
    GroupLabel,
    Section,
    SegRow,
    SliderField,
    ToggleRow,
    chipBtnClass,
    inputClass,
    swatchBtnClass,
} from './QrUiBits';
import Hint from '../../../../../components/common/Hint';

const FRAME_LABEL_PRESETS = ['Scan Me', 'Scan & Pay', 'Visit Website', 'Download'];

const PATTERN_ICONS = {
    none: IconBan,
    dots: IconGridDots,
    grid: IconGrid4x4,
    diagonal: IconSlash,
};

/**
 * Grid of thumbnail buttons — the picker used for every shape catalogue.
 * Auto-fill keeps the tiles at thumbnail size instead of stretching them when
 * the sidebar is wide, so a 25-shape catalogue stays a few rows tall.
 */
function SwatchGrid({ options, value, onChange, tile = 52 }) {
    return (
        <div
            className="grid gap-1.5"
            style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${tile}px, 1fr))` }}
        >
            {options.map((opt) => (
                <Hint label={opt.label} key={opt.value}>
                <button
                    type="button"
                    onClick={() => onChange(opt.value)}
                    aria-label={opt.label}
                    aria-pressed={opt.value === value}
                    className={swatchBtnClass(opt.value === value)}
                    style={{ height: tile }}
                >
                    {opt.thumb}
                </button>
                </Hint>
            ))}
        </div>
    );
}

/**
 * Every design control for the QR generator, grouped into collapsible cards:
 * shape, colour, logo, frame and advanced. Each change goes through `patch` and
 * the parent re-renders the live preview.
 */
export default function QrDesignPanel({
    design,
    patch,
    logoName,
    onLogoPick,
    onLogoRemove,
    onLogoSelect,
}) {
    const { t } = useTranslation();
    const logoInputRef = useRef(null);
    const bgInputRef = useRef(null);
    const hasFrame = design.frameType !== 'none';

    // Dropping a file onto the upload button works like picking one; the file
    // type is still checked by the parent's picker.
    const [dragging, setDragging] = useState(false);
    const onDragEnter = (e) => { e.preventDefault(); setDragging(true); };
    const onDragLeave = (e) => { e.preventDefault(); setDragging(false); };
    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        onLogoPick(e.dataTransfer?.files);
    };

    const readBgImage = (files) => {
        const file = Array.from(files || [])[0];
        if (!file || !file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = () => patch({ bgImageDataUrl: String(reader.result) });
        reader.readAsDataURL(file);
    };

    const [bgDragging, setBgDragging] = useState(false);
    const onBgDragEnter = (e) => { e.preventDefault(); setBgDragging(true); };
    const onBgDragLeave = (e) => { e.preventDefault(); setBgDragging(false); };
    const onBgDrop = (e) => {
        e.preventDefault();
        setBgDragging(false);
        readBgImage(e.dataTransfer?.files);
    };

    // One accordion: opening a section closes whatever was open before.
    const [openSection, setOpenSection] = useState('shape');
    const sectionProps = useCallback((id) => ({
        open: openSection === id,
        onToggle: () => setOpenSection((current) => (current === id ? null : id)),
    }), [openSection]);

    const dotOptions = useMemo(() => DOT_STYLES.map((style) => ({
        value: style,
        label: t(`qrCode.dotStyles.${style}`),
        thumb: <DotThumb style={style} />,
    })), [t]);

    const eyeFrameOptions = useMemo(() => EYE_FRAME_STYLES.map((style) => ({
        value: style,
        label: t(`qrCode.eyeFrameStyles.${style}`),
        thumb: <EyeFrameThumb style={style} />,
    })), [t]);

    const eyeBallOptions = useMemo(() => EYE_BALL_STYLES.map((style) => ({
        value: style,
        label: t(`qrCode.eyeBallStyles.${style}`),
        thumb: <EyeBallThumb style={style} />,
    })), [t]);

    const frameOptions = useMemo(() => FRAME_TYPES.map((type) => ({
        value: type,
        label: t(`qrCode.frames.${type}`),
        thumb: <FrameThumb type={type} />,
    })), [t]);

    const gradientTypeOptions = useMemo(() => GRADIENT_TYPES.map((type) => ({
        value: type,
        label: t(`qrCode.gradientType.${type}`),
    })), [t]);

    const errorLevelOptions = useMemo(() => ERROR_LEVELS.map((level) => ({
        value: level,
        label: level,
    })), []);

    // Drawn rather than named: the four patterns are easier to tell apart as
    // marks than as words, and the name stays in the tooltip.
    const patternOptions = useMemo(() => BG_PATTERNS.map((pattern) => {
        const Icon = PATTERN_ICONS[pattern];
        return {
            value: pattern,
            title: t(`qrCode.bgPatterns.${pattern}`),
            label: <Icon size={18} stroke={1.8} />,
        };
    }), [t]);

    const applyPalette = ([fg, bg]) => patch({ fgColor: fg, bgColor: bg, gradientEnabled: false, transparentBg: false });

    return (
        <>
            <Section
                title={t('qrCode.shapeTitle')}
                summary={t(`qrCode.dotStyles.${design.dotStyle}`)}
                {...sectionProps('shape')}
            >
                <div>
                    <GroupLabel>{t('qrCode.bodyShape')}</GroupLabel>
                    <SwatchGrid options={dotOptions} value={design.dotStyle} onChange={(v) => patch({ dotStyle: v })} />
                </div>
                <div>
                    <GroupLabel>{t('qrCode.eyeFrame')}</GroupLabel>
                    <SwatchGrid options={eyeFrameOptions} value={design.eyeFrameStyle} onChange={(v) => patch({ eyeFrameStyle: v })} />
                </div>
                <div>
                    <GroupLabel>{t('qrCode.eyeBall')}</GroupLabel>
                    <SwatchGrid options={eyeBallOptions} value={design.eyeBallStyle} onChange={(v) => patch({ eyeBallStyle: v })} />
                </div>
            </Section>

            <Section
                title={t('qrCode.designTitle')}
                summary={(
                    <>
                        <span
                            className="h-4 w-4 rounded-[5px] border border-[#ECE8E4] dark:border-zinc-700"
                            style={{ background: design.gradientEnabled
                                ? `linear-gradient(135deg, ${design.gradientFrom}, ${design.gradientTo})`
                                : design.fgColor }}
                        />
                        <span
                            className="h-4 w-4 rounded-[5px] border border-[#ECE8E4] dark:border-zinc-700"
                            style={{ background: design.transparentBg ? 'transparent' : design.bgColor }}
                        />
                        {design.bgImageDataUrl ? (
                            <span className="ml-0.5 truncate">{t('qrCode.backgroundImageOn')}</span>
                        ) : design.bgPattern && design.bgPattern !== 'none' ? (
                            <span className="ml-0.5 truncate">{t(`qrCode.bgPatterns.${design.bgPattern}`)}</span>
                        ) : null}
                    </>
                )}
                {...sectionProps('colors')}
            >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {design.gradientEnabled ? (
                        <ColorField label={t('qrCode.gradientFrom')} value={design.gradientFrom} onChange={(v) => patch({ gradientFrom: v })} />
                    ) : (
                        <ColorField label={t('qrCode.fgColor')} value={design.fgColor} onChange={(v) => patch({ fgColor: v })} />
                    )}
                    {design.transparentBg ? null : (
                        <ColorField label={t('qrCode.bgColor')} value={design.bgColor} onChange={(v) => patch({ bgColor: v })} />
                    )}
                </div>

                <div>
                    <GroupLabel>{t('qrCode.palettes')}</GroupLabel>
                    <div className="flex flex-wrap gap-2">
                        {COLOR_PALETTES.map(([fg, bg]) => {
                            const active = !design.gradientEnabled && design.fgColor === fg && design.bgColor === bg;
                            return (
                                <button
                                    key={`${fg}${bg}`}
                                    type="button"
                                    onClick={() => applyPalette([fg, bg])}
                                    aria-label={`${fg} / ${bg}`}
                                    aria-pressed={active}
                                    className={`h-[38px] w-[38px] rounded-[11px] transition-all ${
                                        active
                                            ? 'border-2 border-primary ring-[3px] ring-primary/15'
                                            : 'border border-[#ECE8E4] hover:border-primary/40 dark:border-zinc-700'
                                    }`}
                                    style={{ background: `linear-gradient(135deg, ${fg} 0 50%, ${bg} 50% 100%)` }}
                                />
                            );
                        })}
                    </div>
                </div>

                <ToggleRow
                    label={t('qrCode.gradient')}
                    hint={t('qrCode.gradientHint')}
                    checked={design.gradientEnabled}
                    onChange={(v) => patch({ gradientEnabled: v })}
                />
                {design.gradientEnabled ? (
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <ColorField label={t('qrCode.gradientTo')} value={design.gradientTo} onChange={(v) => patch({ gradientTo: v })} />
                            <Field label={t('qrCode.gradientTypeLabel')}>
                                <SegRow
                                    options={gradientTypeOptions}
                                    value={design.gradientType}
                                    onChange={(v) => patch({ gradientType: v })}
                                    ariaLabel={t('qrCode.gradientTypeLabel')}
                                />
                            </Field>
                        </div>
                        {design.gradientType === 'linear' ? (
                            <SliderField
                                label={t('qrCode.gradientRotation')}
                                value={design.gradientRotation}
                                min={0}
                                max={360}
                                step={15}
                                suffix="°"
                                display={toBnDigits(design.gradientRotation)}
                                onChange={(v) => patch({ gradientRotation: v })}
                            />
                        ) : null}
                    </div>
                ) : null}

                <ToggleRow
                    label={t('qrCode.transparentBg')}
                    hint={t('qrCode.transparentBgHint')}
                    checked={design.transparentBg}
                    onChange={(v) => patch({ transparentBg: v })}
                />
                <ToggleRow
                    label={t('qrCode.customEyeColors')}
                    checked={design.customEyeColors}
                    onChange={(v) => patch({ customEyeColors: v })}
                />
                {design.customEyeColors ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <ColorField label={t('qrCode.eyeFrameColor')} value={design.eyeFrameColor} onChange={(v) => patch({ eyeFrameColor: v })} />
                        <ColorField label={t('qrCode.eyeBallColor')} value={design.eyeBallColor} onChange={(v) => patch({ eyeBallColor: v })} />
                    </div>
                ) : null}

                <div className="border-t border-[#F2EFEC] pt-4 dark:border-zinc-800">
                    <div className="mb-3 text-[13px] font-bold text-[#1C1815] dark:text-zinc-100">{t('qrCode.backgroundTitle')}</div>
                    <div className="space-y-4">
                        <div>
                            <GroupLabel>{t('qrCode.bgPattern')}</GroupLabel>
                            <SegRow
                                options={patternOptions}
                                value={design.bgPattern || 'none'}
                                onChange={(v) => patch({ bgPattern: v })}
                                ariaLabel={t('qrCode.bgPattern')}
                            />
                        </div>

                        <input
                            ref={bgInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={(e) => { readBgImage(e.target.files); e.target.value = ''; }}
                        />
                        <div className="flex items-center gap-2.5">
                            <button
                                type="button"
                                onClick={() => bgInputRef.current?.click()}
                                onDragEnter={onBgDragEnter}
                                onDragOver={onBgDragEnter}
                                onDragLeave={onBgDragLeave}
                                onDrop={onBgDrop}
                                className={`flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border-[1.5px] border-dashed text-[13.5px] font-semibold transition-colors ${
                                    bgDragging
                                        ? 'border-primary bg-primary-50 text-primary-700 dark:bg-primary-950/30 dark:text-primary-400'
                                        : 'border-[#ECE8E4] bg-[#FBFAF9] text-[#3D3833] hover:border-primary/50 hover:text-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300'
                                }`}
                            >
                                <IconPhoto size={16} stroke={1.8} />
                                {bgDragging ? t('qrCode.logoDropHere') : t('qrCode.backgroundImage')}
                            </button>
                            {design.bgImageDataUrl ? (
                                <button
                                    type="button"
                                    onClick={() => patch({ bgImageDataUrl: null })}
                                    className="h-11 rounded-xl border border-[#ECE8E4] bg-white px-3.5 text-[13px] font-semibold text-[#D33A2C] transition-colors hover:border-[#D33A2C]/40 dark:border-zinc-700 dark:bg-zinc-900"
                                >
                                    {t('qrCode.removeImage')}
                                </button>
                            ) : null}
                        </div>

                        {design.bgImageDataUrl ? (
                            <div className="space-y-2">
                                <SliderField
                                    label={t('qrCode.backgroundOpacity')}
                                    value={design.bgImageOpacity}
                                    min={5}
                                    max={60}
                                    step={5}
                                    suffix="%"
                                    display={toBnDigits(design.bgImageOpacity)}
                                    onChange={(v) => patch({ bgImageOpacity: v })}
                                />
                                <p className="text-[11.5px] text-[#6B635B] dark:text-zinc-400">{t('qrCode.backgroundOpacityHint')}</p>
                            </div>
                        ) : (
                            <p className="text-[11.5px] text-[#6B635B] dark:text-zinc-400">{t('qrCode.backgroundHint')}</p>
                        )}
                    </div>
                </div>

                <div className="space-y-4 border-t border-[#F2EFEC] pt-4 dark:border-zinc-800">
                    <div>
                        <SliderField
                            label={t('qrCode.cornerRadius')}
                            value={design.cornerRadiusPercent}
                            min={0}
                            max={18}
                            suffix="%"
                            display={toBnDigits(design.cornerRadiusPercent)}
                            onChange={(v) => patch({ cornerRadiusPercent: v })}
                        />
                        <p className="mt-2 text-[11.5px] text-[#6B635B] dark:text-zinc-400">{t('qrCode.cornerRadiusHint')}</p>
                    </div>
                    <SliderField
                        label={t('qrCode.frameSpacing')}
                        value={design.quietZonePercent}
                        min={0}
                        max={12}
                        step={0.5}
                        suffix="%"
                        display={toBnDigits(design.quietZonePercent)}
                        onChange={(v) => patch({ quietZonePercent: v })}
                    />
                </div>
            </Section>

            <Section
                title={t('qrCode.logoTitle')}
                summary={design.logoDataUrl ? (logoName || t('qrCode.logoOn')) : t('qrCode.logoNone')}
                {...sectionProps('logo')}
            >
                <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml,.png,.jpg,.jpeg,.webp,.svg"
                    className="hidden"
                    onChange={(e) => { onLogoPick(e.target.files); e.target.value = ''; }}
                />

                <div>
                    <GroupLabel>{t('qrCode.logoPreset')}</GroupLabel>
                    <div className="flex flex-wrap gap-2.5">
                        <button
                            type="button"
                            onClick={() => onLogoSelect('none')}
                            aria-pressed={!design.logoDataUrl}
                            className={`${swatchBtnClass(!design.logoDataUrl)} h-[54px] px-4 text-[13px] font-semibold`}
                        >
                            {t('qrCode.logoNone')}
                        </button>
                        {LOGO_PRESETS.map((preset) => (
                            <Hint label={t(`qrCode.logoNames.${preset.id}`)} key={preset.id}>
                            <button
                                type="button"
                                onClick={() => onLogoSelect(preset.id)}
                                aria-label={t(`qrCode.logoNames.${preset.id}`)}
                                aria-pressed={design.logoId === preset.id}
                                className={`${swatchBtnClass(design.logoId === preset.id)} h-[54px] w-[54px]`}
                            >
                                <LogoPresetTile
                                    preset={preset}
                                    size={44}
                                    {...(design.logoId === preset.id
                                        ? { bg: design.logoBgColor, icon: design.logoIconColor, plate: design.logoPlate }
                                        : {})}
                                />
                            </button>
                            </Hint>
                        ))}
                        <button
                            type="button"
                            onClick={() => logoInputRef.current?.click()}
                            onDragEnter={onDragEnter}
                            onDragOver={onDragEnter}
                            onDragLeave={onDragLeave}
                            onDrop={onDrop}
                            aria-label={t('qrCode.uploadLogo')}
                            aria-pressed={design.logoId === 'custom' && !!design.logoDataUrl}
                            className={`flex h-[54px] items-center justify-center gap-2 rounded-xl border-[1.5px] border-dashed px-4 text-[13px] font-semibold transition-colors ${
                                dragging
                                    ? 'border-primary bg-primary-50 text-primary-700 dark:bg-primary-950/30 dark:text-primary-400'
                                    : 'border-[#ECE8E4] bg-[#FBFAF9] text-[#3D3833] hover:border-primary/50 hover:text-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300'
                            }`}
                        >
                            <IconPlus size={17} stroke={1.8} />
                            {dragging ? t('qrCode.logoDropHere') : t('qrCode.uploadLogo')}
                        </button>
                        {design.logoDataUrl ? (
                            <button
                                type="button"
                                onClick={onLogoRemove}
                                className="h-[54px] rounded-xl border border-[#ECE8E4] bg-white px-4 text-[13px] font-semibold text-[#D33A2C] transition-colors hover:border-[#D33A2C]/40 dark:border-zinc-700 dark:bg-zinc-900"
                            >
                                {t('qrCode.removeLogo')}
                            </button>
                        ) : null}
                    </div>
                </div>

                {design.logoId === 'custom' && design.logoDataUrl ? (
                    <div className="flex items-center gap-2 rounded-lg border border-[#ECE8E4] bg-[#F7F5F3] px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800">
                        <IconPhoto size={18} className="flex-none text-primary" />
                        <span className="truncate text-[12.5px] font-medium text-[#3D3833] dark:text-zinc-200">{logoName}</span>
                        <button
                            type="button"
                            onClick={onLogoRemove}
                            className="ml-auto rounded-md p-1 text-[#A8A099] hover:bg-white hover:text-[#D33A2C] dark:hover:bg-zinc-900"
                            aria-label={t('qrCode.removeLogo')}
                        >
                            <IconX size={16} />
                        </button>
                    </div>
                ) : null}

                {findLogoPreset(design.logoId) ? (
                    <div className="space-y-3">
                        <ToggleRow
                            label={t('qrCode.logoPlate')}
                            hint={t('qrCode.logoPlateHint')}
                            checked={design.logoPlate}
                            onChange={(v) => patch({ logoPlate: v })}
                        />
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <ColorField
                                label={design.logoPlate ? t('qrCode.logoBgColor') : t('qrCode.logoIconOnlyColor')}
                                value={design.logoBgColor}
                                onChange={(v) => patch({ logoBgColor: v })}
                            />
                            {design.logoPlate ? (
                                <ColorField label={t('qrCode.logoIconColor')} value={design.logoIconColor} onChange={(v) => patch({ logoIconColor: v })} />
                            ) : null}
                        </div>
                    </div>
                ) : null}

                {design.logoDataUrl ? (
                    <div className="space-y-3">
                        <SliderField
                            label={t('qrCode.logoSize')}
                            value={design.logoSizePercent}
                            min={10}
                            max={40}
                            suffix="%"
                            display={toBnDigits(design.logoSizePercent)}
                            onChange={(v) => patch({ logoSizePercent: v })}
                        />
                        <SliderField
                            label={t('qrCode.logoRadius')}
                            value={design.logoRadiusPercent}
                            min={0}
                            max={50}
                            step={2}
                            suffix="%"
                            display={toBnDigits(design.logoRadiusPercent)}
                            onChange={(v) => patch({ logoRadiusPercent: v })}
                        />
                        <ToggleRow
                            label={t('qrCode.hideBgDots')}
                            hint={t('qrCode.hideBgDotsHint')}
                            checked={design.hideBackgroundDots}
                            onChange={(v) => patch({ hideBackgroundDots: v })}
                        />
                        {/* Margin only widens the cleared area behind the logo, so it
                            does nothing unless the dots behind it are being removed. */}
                        {design.hideBackgroundDots ? (
                            <div className="border-l-2 border-[#ECE8E4] pl-3 dark:border-zinc-700">
                                <SliderField
                                    label={t('qrCode.logoMargin')}
                                    value={design.logoMarginPercent}
                                    min={0}
                                    max={6}
                                    step={0.5}
                                    suffix="%"
                                    display={toBnDigits(design.logoMarginPercent)}
                                    onChange={(v) => patch({ logoMarginPercent: v })}
                                />
                            </div>
                        ) : null}
                        <p className="text-[11.5px] text-[#6B635B] dark:text-zinc-400">{t('qrCode.logoScanHint')}</p>
                    </div>
                ) : (
                    <p className="text-[11.5px] text-[#6B635B] dark:text-zinc-400">{t('qrCode.logoHint')}</p>
                )}
            </Section>

            <Section
                title={t('qrCode.frameTitle')}
                summary={hasFrame ? t(`qrCode.frames.${design.frameType}`) : t('qrCode.frames.none')}
                {...sectionProps('frame')}
            >
                <div>
                    <GroupLabel>{t('qrCode.frameStyle')}</GroupLabel>
                    <SwatchGrid
                        options={frameOptions}
                        value={design.frameType}
                        onChange={(v) => patch({ frameType: v })}
                        tile={62}
                    />
                </div>

                {hasFrame ? (
                    <div className="space-y-3">
                        <Field label={t('qrCode.frameLabel')}>
                            <input
                                type="text"
                                value={design.frameLabel}
                                onChange={(e) => patch({ frameLabel: e.target.value })}
                                placeholder={t('qrCode.frameLabelPlaceholder')}
                                className={inputClass}
                            />
                        </Field>
                        <div className="flex flex-wrap gap-2">
                            {FRAME_LABEL_PRESETS.map((preset) => (
                                <button
                                    key={preset}
                                    type="button"
                                    onClick={() => patch({ frameLabel: preset })}
                                    className={chipBtnClass(design.frameLabel === preset)}
                                >
                                    {preset}
                                </button>
                            ))}
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <ColorField label={t('qrCode.frameBorderColor')} value={design.frameBorderColor} onChange={(v) => patch({ frameBorderColor: v })} />
                            <ColorField label={t('qrCode.frameBgColor')} value={design.frameBgColor} onChange={(v) => patch({ frameBgColor: v })} />
                            <ColorField label={t('qrCode.frameLabelBgColor')} value={design.frameLabelBgColor} onChange={(v) => patch({ frameLabelBgColor: v })} />
                            <ColorField label={t('qrCode.frameLabelTextColor')} value={design.frameLabelTextColor} onChange={(v) => patch({ frameLabelTextColor: v })} />
                        </div>
                    </div>
                ) : null}
            </Section>

            <Section
                title={t('qrCode.advancedTitle')}
                summary={`ECC ${design.errorLevel}`}
                {...sectionProps('advanced')}
            >
                <Field label={t('qrCode.errorLevel')} hint={t('qrCode.errorLevelHint')}>
                    <SegRow
                        options={errorLevelOptions}
                        value={design.errorLevel}
                        onChange={(v) => patch({ errorLevel: v })}
                        ariaLabel={t('qrCode.errorLevel')}
                    />
                </Field>
            </Section>
        </>
    );
}
